import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Redirect, Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Layout from "./components/Layout";
import CookieConsentComponent from "@/components/CookieConsent";
import Analytics from "./components/Analytics";
import JsonLd from "./components/JsonLd";

// Enable smooth scroll behavior globally
if (typeof window !== "undefined") {
  document.documentElement.style.scrollBehavior = "smooth";
}

// Pages — carregamento eager para rotas críticas (above the fold)
import Home from "./pages/Home";

// Pages — lazy para reduzir o bundle principal
const Produto = lazy(() => import("./pages/Produto"));
const Sobre = lazy(() => import("./pages/Sobre"));
const Contato = lazy(() => import("./pages/Contato"));
const Blog = lazy(() => import("./pages/Blog"));
// BlogPost carrega streamdown (Shiki + Mermaid + KaTeX) — lazy é essencial aqui
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Privacidade = lazy(() => import("./pages/Privacidade"));
const Termos = lazy(() => import("./pages/Termos"));
const SolicitarDemo = lazy(() => import("./pages/SolicitarDemo"));
const Obrigado = lazy(() => import("./pages/Obrigado"));

function Router() {
  const [location] = useLocation();

  useEffect(() => {
    // Scroll to top with smooth behavior when route changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location]);

  return (
    <>
      <Analytics />
      <JsonLd />
      <Layout>
        <Suspense fallback={null}>
        <Switch>
          {/* ── Rotas principais (PT) ── */}
          <Route path="/" component={Home} />
          <Route path="/produto" component={Produto} />
          <Route path="/sobre" component={Sobre} />
          <Route path="/contato" component={Contato} />
          <Route path="/blog" component={Blog} />
          <Route path="/blog/:id" component={BlogPost} />
          <Route path="/privacidade" component={Privacidade} />
          <Route path="/termos" component={Termos} />
          <Route path="/solicitar-demo" component={SolicitarDemo} />
          <Route path="/obrigado" component={Obrigado} />

          {/* ── Rotas alternativas em Inglês (EN) — redirect para PT ── */}
          <Route path="/product" component={() => <Redirect to="/produto" />} />
          <Route path="/about" component={() => <Redirect to="/sobre" />} />
          <Route path="/contact" component={() => <Redirect to="/contato" />} />
          <Route path="/privacy-policy" component={() => <Redirect to="/privacidade" />} />
          <Route path="/terms-of-use" component={() => <Redirect to="/termos" />} />
          <Route path="/request-demo" component={() => <Redirect to="/solicitar-demo" />} />
          <Route path="/schedule-demo" component={() => <Redirect to="/solicitar-demo" />} />

          {/* ── Rotas alternativas em Espanhol (ES) — redirect para PT ── */}
          <Route path="/producto" component={() => <Redirect to="/produto" />} />
          <Route path="/acerca" component={() => <Redirect to="/sobre" />} />
          <Route path="/contacto" component={() => <Redirect to="/contato" />} />
          <Route path="/privacidad" component={() => <Redirect to="/privacidade" />} />
          <Route path="/terminos" component={() => <Redirect to="/termos" />} />
          <Route path="/solicitar-demostracion" component={() => <Redirect to="/solicitar-demo" />} />

          <Route path="/404" component={NotFound} />
          {/* Final fallback route */}
          <Route component={NotFound} />
        </Switch>
        </Suspense>
      </Layout>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark">
        <TooltipProvider>
          <CookieConsentComponent />
          <Toaster />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
